if (!customElements.get('sticky-add-to-cart')) {
  class StickyAddToCart extends HTMLElement {
    constructor() {
      super();
      this.sectionId = this.dataset.sectionId;
      this.scrollThreshold = 200;
      this.syncInterval = null;
      this.observer = null;
      this.buttonObserver = null;
      this.formButtonsObserver = null;
      this.mediaMagnifyObserver = null;
      this.productVariantsObserver = null;
      this.formButtonsInViewport = true;
      this.mediaMagnifyInViewport = true;
      this.productVariantsInViewport = true;
      this.retryCount = 0;
      this.maxRetries = 20; // Try for 10 seconds (20 * 500ms)
      this.isInitialized = false;
    }

    connectedCallback() {
      this.stickyButton = this.querySelector(`#StickyAddToCart-${this.sectionId}`);
      
      if (!this.stickyButton) return;

      this.setupScrollHandler();
      this.setupViewportObservers();
      this.findMainButton();
    }

    disconnectedCallback() {
      if (this.syncInterval) {
        clearInterval(this.syncInterval);
      }
      if (this.observer) {
        this.observer.disconnect();
      }
      if (this.buttonObserver) {
        this.buttonObserver.disconnect();
      }
      if (this.formButtonsObserver) {
        this.formButtonsObserver.disconnect();
      }
      if (this.mediaMagnifyObserver) {
        this.mediaMagnifyObserver.disconnect();
      }
      if (this.productVariantsObserver) {
        this.productVariantsObserver.disconnect();
      }
      if (this.handleScroll) {
        window.removeEventListener('scroll', this.handleScroll);
      }
    }

    findMainButton() {
      this.mainButton = document.getElementById(`AddToCart-${this.sectionId}`);
      
      if (this.mainButton) {
        this.initialize();
      } else {
        // Button not found, set up observer to watch for it
        this.setupButtonObserver();
        // Also retry periodically as fallback
        this.retryFindButton();
      }
    }

    setupButtonObserver() {
      // Watch the product-info element or document body for the button to appear
      const productInfo = document.querySelector('product-info');
      const watchTarget = productInfo || document.body;
      
      this.buttonObserver = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          // Check if the button was added
          if (mutation.type === 'childList') {
            for (const node of mutation.addedNodes) {
              if (node.nodeType === Node.ELEMENT_NODE) {
                // Check if this node is the button or contains it
                if (node.id === `AddToCart-${this.sectionId}` || 
                    node.querySelector && node.querySelector(`#AddToCart-${this.sectionId}`)) {
                  this.mainButton = document.getElementById(`AddToCart-${this.sectionId}`);
                  if (this.mainButton && !this.isInitialized) {
                    this.initialize();
                    this.buttonObserver.disconnect();
                    return;
                  }
                }
              }
            }
          }
          
          // Also check attributes in case button is shown/hidden
          if (mutation.type === 'attributes' && mutation.target.id === `AddToCart-${this.sectionId}`) {
            if (!this.isInitialized) {
              this.mainButton = document.getElementById(`AddToCart-${this.sectionId}`);
              if (this.mainButton) {
                this.initialize();
                this.buttonObserver.disconnect();
                return;
              }
            }
          }
        }
      });

      this.buttonObserver.observe(watchTarget, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['id', 'style', 'class']
      });
    }

    retryFindButton() {
      if (this.isInitialized) return;
      
      if (this.retryCount < this.maxRetries) {
        this.retryCount++;
        setTimeout(() => {
          this.mainButton = document.getElementById(`AddToCart-${this.sectionId}`);
          if (this.mainButton) {
            this.initialize();
            if (this.buttonObserver) {
              this.buttonObserver.disconnect();
            }
          } else {
            this.retryFindButton();
          }
        }, 500);
      }
    }

    initialize() {
      if (this.isInitialized || !this.mainButton || !this.stickyButton) return;
      
      this.isInitialized = true;
      this.setupClickHandler();
      this.setupStateSync();
      this.syncButtonState();
    }

    setupClickHandler() {
      // Remove any existing listeners
      const newStickyButton = this.stickyButton.cloneNode(true);
      this.stickyButton.parentNode.replaceChild(newStickyButton, this.stickyButton);
      this.stickyButton = newStickyButton;
      
      this.stickyButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        // Re-find button in case it was recreated
        const currentMainButton = document.getElementById(`AddToCart-${this.sectionId}`);
        if (!currentMainButton) return;
        
        // Check if button text indicates "Notify Me When Available" - cache query result
        const mainAtcText = currentMainButton.querySelector('.atc-text');
        const buttonText = mainAtcText ? mainAtcText.textContent.trim() : currentMainButton.textContent.trim();
        
        if (buttonText.toLowerCase().includes('notify me')) {
          // Click the notify me popup trigger instead
          const notifyMeTrigger = document.querySelector('.notify-me-popup-trigger');
          if (notifyMeTrigger) {
            notifyMeTrigger.click();
          }
          return;
        }
        
        // Skip if button is disabled (shouldn't happen for available products, but safety check)
        if (currentMainButton.disabled) {
          return;
        }
        
        // Trigger click on main button - this will handle all logic including popups
        // Direct click without manipulating disabled state for better performance
        currentMainButton.click();
      });
    }

    setupViewportObservers() {
      const formButtons = document.querySelector('.product-form__buttons');
      const mediaMagnify = document.querySelector('media-gallery');
      const productVariants = document.querySelector('.product__variants');
      
      // Check initial state
      const checkIfInViewport = (element) => {
        if (!element) return false;
        const rect = element.getBoundingClientRect();
        return rect.top < window.innerHeight && rect.bottom > 0;
      };
      
      // Set initial states
      this.formButtonsInViewport = checkIfInViewport(formButtons);
      this.mediaMagnifyInViewport = checkIfInViewport(mediaMagnify);
      this.productVariantsInViewport = checkIfInViewport(productVariants);
      this.updateVisibility();
      
      // Observe form buttons
      if (formButtons && typeof IntersectionObserver !== 'undefined') {
        const self = this;
        this.formButtonsObserver = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            self.formButtonsInViewport = entry.isIntersecting;
            self.updateVisibility();
          });
        }, { root: null, rootMargin: '0px', threshold: 0 });
        this.formButtonsObserver.observe(formButtons);
      }
      
      // Observe media magnify
      if (mediaMagnify && typeof IntersectionObserver !== 'undefined') {
        const self = this;
        this.mediaMagnifyObserver = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            self.mediaMagnifyInViewport = entry.isIntersecting;
            self.updateVisibility();
          });
        }, { root: null, rootMargin: '0px', threshold: 0 });
        this.mediaMagnifyObserver.observe(mediaMagnify);
      }
      
      // Observe product variants
      if (productVariants && typeof IntersectionObserver !== 'undefined') {
        const self = this;
        this.productVariantsObserver = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            self.productVariantsInViewport = entry.isIntersecting;
            self.updateVisibility();
          });
        }, { root: null, rootMargin: '0px', threshold: 0 });
        this.productVariantsObserver.observe(productVariants);
      }
    }

    updateVisibility() {
      // Show sticky only when ALL THREE elements are NOT in viewport
      if (!this.formButtonsInViewport && !this.mediaMagnifyInViewport && !this.productVariantsInViewport) {
        this.classList.add('is-visible');
      } else {
        this.classList.remove('is-visible');
      }
    }

    setupScrollHandler() {
      this.handleScroll = () => {
        this.updateVisibility();
      };

      window.addEventListener('scroll', this.handleScroll, { passive: true });
      this.handleScroll(); // Initial check
    }

    setupStateSync() {
      // Watch for changes to main button
      this.observer = new MutationObserver(() => {
        this.syncButtonState();
      });

      if (this.mainButton) {
        this.observer.observe(this.mainButton, { 
          attributes: true, 
          attributeFilter: ['class'],
          childList: true,
          subtree: true,
          characterData: true
        });
      }

      // Sync more frequently to catch text changes
      this.syncInterval = setInterval(() => {
        this.syncButtonState();
      }, 200);
    }

    syncButtonState() {
      // Re-find main button in case it was recreated
      const currentMainButton = document.getElementById(`AddToCart-${this.sectionId}`);
      if (!currentMainButton || !this.stickyButton) {
        // Update reference if button was recreated
        if (currentMainButton && currentMainButton !== this.mainButton) {
          this.mainButton = currentMainButton;
          // Re-setup observer for new button
          if (this.observer) {
            this.observer.disconnect();
          }
          this.observer = new MutationObserver(() => {
            this.syncButtonState();
          });
          this.observer.observe(this.mainButton, { 
            attributes: true, 
            attributeFilter: ['class'],
            childList: true,
            subtree: true,
            characterData: true
          });
        }
        return;
      }
      
      // Sticky button should never be disabled - always allow clicks
      // This ensures "Notify Me When Available" popup works
      this.stickyButton.disabled = false;
      
      // Sync button text - check both .atc-text and direct textContent
      const mainAtcText = currentMainButton.querySelector('.atc-text');
      const stickyAtcText = this.stickyButton.querySelector('.atc-text');
      if (mainAtcText && stickyAtcText) {
        const mainText = mainAtcText.textContent.trim();
        const stickyText = stickyAtcText.textContent.trim();
        if (mainText !== stickyText) {
          stickyAtcText.textContent = mainText;
        }
      } else if (!mainAtcText && !stickyAtcText) {
        // Fallback: sync direct textContent if no .atc-text elements
        const mainText = currentMainButton.textContent.trim();
        const stickyText = this.stickyButton.textContent.trim();
        if (mainText !== stickyText && !mainText.includes('loading')) {
          this.stickyButton.textContent = mainText;
        }
      }
      
      // Sync loader state
      const mainLoader = currentMainButton.querySelector('.loading__spinner');
      const stickyLoader = this.stickyButton.querySelector('.loading__spinner');
      if (mainLoader && stickyLoader) {
        const mainLoaderHidden = mainLoader.classList.contains('hidden');
        const stickyLoaderHidden = stickyLoader.classList.contains('hidden');
        if (mainLoaderHidden !== stickyLoaderHidden) {
          stickyLoader.classList.toggle('hidden', mainLoaderHidden);
        }
      }
      
      // Sync button styles
      const mainButtonStyles = window.getComputedStyle(currentMainButton);
      this.stickyButton.style.color = mainButtonStyles.color;
      this.stickyButton.style.backgroundColor = mainButtonStyles.backgroundColor;
    }
  }

  customElements.define('sticky-add-to-cart', StickyAddToCart);
}
  